'use client'

import { processCardAcquisition } from '@/app/actions/cardAcquisition'
import { processPayment } from '@/app/actions/payment'
import {
	AmountInput,
	Footer,
	IdentificationPanel,
	Logo,
	NorthernLights,
	POISelector,
	PaymentButton,
} from '@/src/components'
import type { AcquiredCard } from '@/src/core/adyen/acquiredCard'
import type { IdentificationData } from '@/src/core/types/identification.types'
import { clientLogger } from '@/src/core/utils/clientLogger'
import { generateServiceId } from '@/src/core/utils/serviceId'
import { withTimeout } from '@/src/core/utils/timeout'
import { TerminalApiResponse } from '@adyen/api-library/lib/src/typings/terminal/terminalApiResponse'
import { AnimatePresence, motion } from 'framer-motion'
import dynamic from 'next/dynamic'
import Link from 'next/link'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { z } from 'zod'
import { usePaymentForm } from '../hooks'
import { useLoyaltyCheck } from '../hooks/useLoyaltyCheck'
import { useTimeout } from '../hooks/useTimeout'

const log = clientLogger.withContext('IndexPage')

// Lazy load heavy components
const ReactConfetti = dynamic(() => import('react-confetti'), {
	ssr: false,
})

const AdyenResults = dynamic(
	async () => {
		const module_ = await import('@/src/components')
		return { default: module_.AdyenResults }
	},
	{
		ssr: false,
		loading: () => <div className="animate-pulse">Loading results...</div>,
	}
)

const RESET_IDENTIFIED_DELAY_MS = 7000

const purchaseAmountSchema = z.number().min(0).max(10_000)

const CONFETTI_COLORS = [
	'#c0fff1',
	'#b6ffe7',
	'#acffdd',
	'#a2ffd3',
	'#98ffc9',
	'#8efebf',
	'#7aeaab',
	'#70e0a1',
	'#66d697',
]

const CONFETTI_ANIMATION = {
	INITIAL: { opacity: 0 },
	ANIMATE: { opacity: 1 },
	EXIT: { opacity: 0 },
	TRANSITION: { duration: 1 },
}

const HEADING_ANIMATION = {
	INITIAL: { opacity: 0, x: -150, y: -150, scale: 0 },
	ANIMATE: { opacity: 1, x: 0, y: 0, scale: 1 },
	EXIT: { opacity: 0, x: -50, y: -50 },
	TRANSITION: { duration: 2, type: 'spring', stiffness: 100, damping: 20, mass: 2 },
}

export default function IndexPage() {
	const [paymentLoading, setPaymentLoading] = useState(false)
	const [acquisitionLoading, setAcquisitionLoading] = useState(false)
	const [terminalResponse, setTerminalResponse] = useState<TerminalApiResponse>()
	const [acquiredCard, setAcquiredCard] = useState<AcquiredCard>()
	// Use the payment form hook for better validation and state management
	const {
		values: { amount: purchaseAmount, poiId: selectedPOI },
		errors: formErrors,
		updateField,
		setFieldError,
	} = usePaymentForm({
		initialValues: { amount: 10, poiId: '' },
	})
	const [identified, setIdentified] = useState(false)
	const [customerName, setCustomerName] = useState('')
	const [responseHidden, setResponseHidden] = useState(true)
	const [, setCurrentServiceId] = useState('')
	const [error, setError] = useState('')

	// Refs for focus management
	const debugButtonReference = useRef<HTMLButtonElement>(null)

	const { setTimeout: setManagedTimeout, clearAllTimeouts } = useTimeout()

	useEffect(() => {
		setCurrentServiceId(generateServiceId())
	}, [])

	/** Sets the identified state to true and resets it after a specified duration. */
	const setIdentifiedAndReset = useCallback(
		(name = 'Anonymous Customer') => {
			setIdentified(true)
			setCustomerName(name)

			// Only reset the welcome message animation, keep customerName for IdentificationPanel
			setManagedTimeout(() => {
				setIdentified(false)
			}, RESET_IDENTIFIED_DELAY_MS)
		},
		[setManagedTimeout]
	)

	/** Handle customer identification from loyalty check */
	const handleCustomerIdentified = useCallback(
		(name: string, idData: IdentificationData) => {
			log.info('Customer identified from loyalty check', { customerName: name, identificationData: idData })
			setIdentifiedAndReset(name)
		},
		[setIdentifiedAndReset]
	)

	/** Handle customer not found */
	const handleCustomerNotFound = useCallback(() => {
		log.info('Customer not found in loyalty check')
		setIdentified(false)
		setCustomerName('')
	}, [])

	// Use the improved loyalty check hook - get identificationData from hook return
	const { identificationData, reset: resetLoyaltyCheck } = useLoyaltyCheck(terminalResponse, {
		onCustomerIdentified: handleCustomerIdentified,
		onCustomerNotFound: handleCustomerNotFound,
		enabled: !!terminalResponse,
	})

	// Reset state when terminal changes
	useEffect(() => {
		setTerminalResponse(undefined)
		setAcquiredCard(undefined)
	}, [selectedPOI])

	// Cleanup all timeouts on unmount
	useEffect(() => {
		return () => {
			clearAllTimeouts()
		}
	}, [clearAllTimeouts])

	/** Handles the payment request with input validation and error handling. */
	const handlePaymentRequest = useCallback(async () => {
		const requestId = crypto.randomUUID()

		log.info('Starting payment request', {
			requestId,
			amount: purchaseAmount,
			selectedPOI,
			hasAcquiredCard: !!acquiredCard,
		})

		try {
			setError('')
			setTerminalResponse(undefined) // Clear previous session data
			resetLoyaltyCheck() // Clear identification panel
			const validatedAmount = purchaseAmountSchema.parse(purchaseAmount)

			if (!selectedPOI) {
				log.warn('Payment request failed - no terminal selected', { requestId })
				setFieldError('poiId', 'Select a terminal first.')
				return
			}

			const newServiceId = generateServiceId()
			setCurrentServiceId(newServiceId)
			setPaymentLoading(true)

			// If an acquired card is set, the payment charges the already-acquired card (no re-tap)
			const result = await withTimeout(
				processPayment({
					amount: validatedAmount,
					POIID: selectedPOI,
					serviceId: newServiceId,
					acquiredCard,
				}),
				30_000,
				'Payment request timed out'
			)

			if (!result.success) {
				throw new Error(result.error)
			}

			setTerminalResponse(result.data.response)

			// Clear the acquired card after successful payment (buttons return to normal)
			// Keep customerName so it stays visible in the IdentificationPanel
			setAcquiredCard(undefined)
		} catch (error) {
			if (error instanceof z.ZodError) {
				setFieldError('amount', 'Enter a valid amount (0–10,000).')
				return
			}

			// Handle specific error messages
			const errorMessage = error instanceof Error ? error.message : 'An unknown error occurred'
			if (errorMessage.includes('timed out')) {
				setError('Payment timed out. Try again.')
			} else if (errorMessage.includes('Invalid response')) {
				setError('Terminal not responding. Check connection and retry.')
			} else if (errorMessage.includes('Terminal rejected')) {
				// Surface the decoded reject reason from Adyen (terminal offline, busy, ...)
				setError(errorMessage)
			} else {
				setError('Payment failed. Try again.')
			}

			log.error('Payment request failed', error)
		} finally {
			setPaymentLoading(false)
		}
	}, [
		purchaseAmount,
		selectedPOI,
		acquiredCard,
		setError,
		setFieldError,
		setCurrentServiceId,
		setTerminalResponse,
		setPaymentLoading,
		resetLoyaltyCheck,
	])

	/** Handles the card acquisition request with input validation and error handling. */
	const handleCardAcquisitionRequest = useCallback(async () => {
		const requestId = crypto.randomUUID()

		log.info('Starting card acquisition request', {
			requestId,
			amount: purchaseAmount,
			selectedPOI,
		})

		try {
			setError('')
			setTerminalResponse(undefined) // Clear previous session data
			resetLoyaltyCheck() // Clear identification panel
			const validatedAmount = purchaseAmountSchema.parse(purchaseAmount)

			if (!selectedPOI) {
				log.warn('Card acquisition request failed - no terminal selected', { requestId })
				setFieldError('poiId', 'Select a terminal first.')
				return
			}

			const newServiceId = generateServiceId()
			setCurrentServiceId(newServiceId)
			setAcquisitionLoading(true)

			const result = await withTimeout(
				processCardAcquisition({
					amount: validatedAmount,
					POIID: selectedPOI,
					serviceId: newServiceId,
				}),
				30_000,
				'Card identification timed out'
			)

			if (!result.success) {
				throw new Error(result.error)
			}

			if (result.data.acquiredCard) {
				log.info('Card acquired', { acquiredCard: result.data.acquiredCard })
			}
			setAcquiredCard(result.data.acquiredCard)
			setTerminalResponse(result.data.response)
		} catch (error) {
			if (error instanceof z.ZodError) {
				setFieldError('amount', 'Enter a valid amount (0–10,000).')
				return
			}

			// Handle specific error messages
			const errorMessage = error instanceof Error ? error.message : 'An unknown error occurred'
			if (errorMessage.includes('timed out')) {
				setError('Identification timed out. Try again.')
			} else if (errorMessage.includes('Invalid response')) {
				setError('Terminal not responding. Check connection and retry.')
			} else {
				setError('Identification failed. Try again.')
			}

			log.error('Card acquisition request failed', error)
		} finally {
			setAcquisitionLoading(false)
		}
	}, [
		purchaseAmount,
		selectedPOI,
		setError,
		setFieldError,
		setCurrentServiceId,
		setTerminalResponse,
		setAcquisitionLoading,
		resetLoyaltyCheck,
	])

	/** Toggles the visibility of the debug response. */
	const handleSetResponseHidden = useCallback(() => {
		setResponseHidden((previousHidden) => !previousHidden)
		// Focus management for debug panel
		if (responseHidden) {
			debugButtonReference.current?.focus()
		}
	}, [responseHidden])

	/** Handles form submission. */
	const handleFormSubmit = useCallback(
		(event: React.FormEvent) => {
			event.preventDefault()
			if (purchaseAmount > 0) {
				void handlePaymentRequest()
			}
		},
		[purchaseAmount, handlePaymentRequest]
	)

	/** Handles amount change. */
	const handleAmountChange = useCallback(
		(value: number) => {
			updateField('amount', value)
		},
		[updateField]
	)

	/** Handles POI selection change. */
	const handlePOIChange = useCallback(
		(poiId: string) => {
			updateField('poiId', poiId)
		},
		[updateField]
	)

	/** Cancels the current card acquisition and resets to initial state. */
	const handleCancel = useCallback(() => {
		log.info('Cancelling card acquisition')
		setAcquiredCard(undefined)
		setTerminalResponse(undefined)
		setIdentified(false)
		setCustomerName('')
		setError('')
	}, [])

	const welcomeMessage = useMemo(
		() =>
			customerName ? (
				<motion.h1
					className="mt-8 text-3xl font-bold tracking-tight text-white sm:mt-14 sm:text-4xl md:mt-16 md:text-6xl"
					initial={HEADING_ANIMATION.INITIAL}
					animate={HEADING_ANIMATION.ANIMATE}
					exit={HEADING_ANIMATION.EXIT}
					transition={HEADING_ANIMATION.TRANSITION}
					role="heading"
					aria-level={1}
				>
					Welcome back,
					<br />
					<span className="bg-gradient-to-r from-[#c0fff1] to-[#66d697] bg-clip-text text-transparent">
						{customerName} <span aria-hidden="true">👋🏼</span>
					</span>
				</motion.h1>
			) : (
				<h1
					className="mt-8 text-3xl font-bold tracking-tight text-white sm:mt-14 sm:text-4xl md:mt-16 md:text-6xl"
					role="heading"
					aria-level={1}
				>
					Discover the Stellar Universe of{' '}
					<span className="bg-gradient-to-r from-[#c0fff1] to-[#66d697] bg-clip-text text-transparent">
						Stell <span aria-hidden="true">✨</span>
					</span>
				</h1>
			),
		[customerName]
	)

	const errorMessage = useMemo(() => {
		if (!error) {
			return
		}
		return (
			<div
				role="alert"
				className="mt-4 rounded-lg bg-red-500/10 p-4 text-sm text-red-200"
				aria-live="assertive"
				aria-atomic="true"
			>
				{error}
			</div>
		)
	}, [error])

	const confetti = useMemo(() => {
		if (!identified) {
			return
		}
		return (
			<motion.div
				initial={CONFETTI_ANIMATION.INITIAL}
				animate={CONFETTI_ANIMATION.ANIMATE}
				exit={CONFETTI_ANIMATION.EXIT}
				transition={CONFETTI_ANIMATION.TRANSITION}
			>
				<ReactConfetti gravity={0.5} colors={CONFETTI_COLORS} />
			</motion.div>
		)
	}, [identified])

	useEffect(() => {
		function handleEscapeKey(event: KeyboardEvent) {
			if (event.key === 'Escape' && !responseHidden) {
				handleSetResponseHidden()
			}
		}

		globalThis.addEventListener('keydown', handleEscapeKey)
		return () => globalThis.removeEventListener('keydown', handleEscapeKey)
	}, [responseHidden, handleSetResponseHidden])

	return (
		<main className="relative flex min-h-screen flex-col" role="main">
			{/* Skip link for keyboard users */}
			<a
				href="#main-content"
				className="sr-only focus:not-sr-only focus:absolute focus:left-4 focus:top-4 focus:z-50 focus:rounded-lg focus:bg-white focus:p-4 focus:text-black focus:outline-none focus:ring-2 focus:ring-[#66d697]"
			>
				Skip to main content
			</a>

			<NorthernLights aria-hidden="true" />

			<div
				id="main-content"
				className="relative isolate mx-auto w-full max-w-7xl grow overflow-hidden px-4 py-6 sm:px-6 sm:py-10 lg:flex lg:px-8 lg:pt-20"
				tabIndex={-1}
			>
				<div className="mx-auto max-w-2xl shrink-0 lg:mx-0 lg:max-w-2xl lg:pt-8">
					<nav aria-label="Main navigation">
						<Link
							href="/"
							title="Return to Stell homepage"
							className="inline-block min-h-[44px] min-w-[44px] focus:outline-none focus:ring-2 focus:ring-[#66d697] focus:ring-offset-2 focus:ring-offset-black"
						>
							<Logo className="h-6 fill-white sm:h-8" aria-label="Stell logo" />
							<span className="sr-only">Stell home</span>
						</Link>
					</nav>

					<AnimatePresence>{welcomeMessage}</AnimatePresence>

					<p className="mt-4 text-sm leading-7 text-white/70 sm:mt-6 sm:text-base sm:leading-8">
						One tap. Identify and pay together. No app required.
					</p>

					{errorMessage}

					<form
						className="mt-6 flex flex-col gap-y-4 sm:mt-10 sm:gap-y-6"
						onSubmit={handleFormSubmit}
						aria-label="Payment form"
					>
						<AmountInput value={purchaseAmount} onChange={handleAmountChange} error={formErrors.amount} />

						<POISelector value={selectedPOI} onChange={handlePOIChange} error={formErrors.poiId} autoFocus />

						<AnimatePresence>
							{identificationData ? (
								<IdentificationPanel identificationData={identificationData} customerName={customerName} />
							) : undefined}
						</AnimatePresence>

						<div className="flex space-x-4">
							<PaymentButton
								onClick={handlePaymentRequest}
								loading={paymentLoading}
								label={
									acquiredCard ? 'Finalize Sale' : purchaseAmount === 0 ? 'Payment not available' : 'Pay'
								}
								loadingLabel="Processing payment..."
								disabled={paymentLoading || acquisitionLoading || selectedPOI === '' || purchaseAmount === 0}
								keyboardShortcut="p"
								aria-keyshortcuts="p"
							/>
							{acquiredCard ? (
								<button
									type="button"
									onClick={handleCancel}
									className="min-h-[44px] rounded-lg border border-white/20 bg-white/5 px-6 py-3 text-sm font-medium text-white/80 transition-colors hover:bg-white/10 hover:text-white focus:outline-none focus:ring-2 focus:ring-[#66d697] focus:ring-offset-2 focus:ring-offset-black"
									aria-label="Cancel card acquisition"
								>
									Cancel
								</button>
							) : (
								<PaymentButton
									onClick={handleCardAcquisitionRequest}
									loading={acquisitionLoading}
									label="Identify Card"
									loadingLabel="Identifying card..."
									disabled={paymentLoading || acquisitionLoading || selectedPOI === ''}
									keyboardShortcut="c"
									aria-keyshortcuts="c"
								/>
							)}
						</div>

						<div className="sr-only" role="note">
							Keyboard shortcuts: Press P to Pay, C to Identify Card
						</div>
					</form>
				</div>
			</div>

			<Footer />

			<button
				ref={debugButtonReference}
				type="button"
				className="fixed right-4 top-4 min-h-[44px] min-w-[44px] rounded-md bg-white/5 px-3 py-2 text-sm text-white/40 transition-colors hover:bg-white/10 hover:text-white focus:outline-none focus:ring-2 focus:ring-[#66d697] focus:ring-offset-2 focus:ring-offset-black"
				onClick={handleSetResponseHidden}
				aria-expanded={!responseHidden}
				aria-controls="debug-response"
				aria-haspopup="dialog"
			>
				<span aria-hidden="true">⚠︎</span> {responseHidden ? 'Show' : 'Hide'} response
			</button>

			{!responseHidden && (
				<div
					id="debug-response"
					className="fixed inset-x-0 bottom-0 max-h-[40vh] overflow-y-auto bg-white/5 backdrop-blur-md"
					role="dialog"
					aria-label="Debug response panel"
					aria-modal="true"
					aria-describedby="debug-response-description"
					tabIndex={0}
				>
					<div id="debug-response-description" className="sr-only">
						This panel shows the technical response from the payment terminal. Press Escape to close.
					</div>
					<AdyenResults formattedResponse={terminalResponse} isPaymentComplete={!!terminalResponse} />
				</div>
			)}

			<AnimatePresence>{confetti}</AnimatePresence>
		</main>
	)
}
